import React, { useState, useEffect, useMemo } from 'react';
import {
  Container, Typography, Grid, Card, CardContent, Button, Box, Skeleton,
  ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import {
  Timer, Today, DateRange, PlayArrow, Assessment, Groups, FolderOpen,
  TrendingUp,
} from '@mui/icons-material';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
} from 'recharts';
import { useNavigate } from 'react-router-dom';
import { startOfWeek, endOfWeek, startOfMonth, format } from 'date-fns';
import { useAuth } from '../context_providers/AuthContext';
import { useNotification } from '../context_providers/NotificationContext';
import { useRBAC } from '../context_providers/RBACContext';
import { useProjects } from '../context_providers/ProjectContext';
import { DashboardStats } from '../../types';
import apiService from '../../utils/api.service';
import LoadingSpinner from '../shared/LoadingSpinner';

// ==================== Constants ====================
const CHART_COLORS = {
  primary: '#7b6df6',
  secondary: '#10b981',
  blue: '#3b82f6',
  amber: '#f59e0b',
  rose: '#f43f5e',
  cyan: '#06b6d4',
  orange: '#f97316',
};

const PIE_COLORS = [CHART_COLORS.primary, '#334155'];
const PROJECT_COLORS = [
  CHART_COLORS.primary, CHART_COLORS.secondary, CHART_COLORS.blue,
  CHART_COLORS.amber, CHART_COLORS.cyan,
];
const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

// ==================== Helpers ====================
const getGreeting = (): string => {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
};

interface DailyChartData {
  day: string;
  hours: number;
}

interface ProjectChartData {
  name: string;
  hours: number;
  color: string;
}

interface BillableData {
  name: string;
  value: number;
}

const formatHours = (minutes: number): number => {
  return Math.round((minutes / 60) * 10) / 10;
};

// ==================== Custom Tooltip ====================
const ChartTooltip: React.FC<any> = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <Box sx={{
      bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider',
      borderRadius: 1.5, px: 2, py: 1, boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
    }}>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      {payload.map((p: any, i: number) => (
        <Typography key={i} variant="body2" fontWeight={600} sx={{ color: p.color || p.fill }}>
          {p.value?.toFixed(1)}h
        </Typography>
      ))}
    </Box>
  );
};

// ==================== Main Component ====================
const DashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showError } = useNotification();
  const { projects } = useProjects();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [weeklyData, setWeeklyData] = useState<DailyChartData[]>([]);
  const [projectData, setProjectData] = useState<ProjectChartData[]>([]);
  const [billableData, setBillableData] = useState<BillableData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [chartsLoading, setChartsLoading] = useState(true);
  const [viewMode, setViewMode] = useState<'personal' | 'team'>('personal');
  const [teamStats, setTeamStats] = useState<any>(null);
  const [teamLoading, setTeamLoading] = useState(false);

  const { hasPermission } = useRBAC();
  const isManager = hasPermission('dashboard', 'read');

  const today = useMemo(() => new Date(), []);
  const weekStart = useMemo(() => startOfWeek(today, { weekStartsOn: 1 }), [today]);
  const weekEnd = useMemo(() => endOfWeek(today, { weekStartsOn: 1 }), [today]);
  const monthStart = useMemo(() => startOfMonth(today), [today]);

  // Fetch dashboard stats
  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        const response = await apiService.getPersonalDashboard();
        setStats(response.data || response);
      } catch (err: any) {
        showError(err.message || 'Failed to load dashboard');
      } finally {
        setIsLoading(false);
      }
    };
    fetchDashboard();
  }, [showError]);

  // Fetch chart data
  useEffect(() => {
    const fetchChartData = async () => {
      setChartsLoading(true);
      try {
        const [weeklyResp, projectResp] = await Promise.allSettled([
          apiService.getReportByDate({
            start_date: format(weekStart, 'yyyy-MM-dd'),
            end_date: format(weekEnd, 'yyyy-MM-dd'),
          }),
          apiService.getReportByProject({
            start_date: format(monthStart, 'yyyy-MM-dd'),
            end_date: format(today, 'yyyy-MM-dd'),
          }),
        ]);

        // Process weekly data into day-by-day chart
        if (weeklyResp.status === 'fulfilled') {
          const raw = weeklyResp.value?.data || weeklyResp.value || {};
          const items = Array.isArray(raw) ? raw : (raw?.dates || []);
          const dayMap: Record<string, number> = {};
          DAY_LABELS.forEach((d) => { dayMap[d] = 0; });

          items.forEach((item: any) => {
            if (item.date) {
              const dateObj = new Date(item.date + 'T00:00:00');
              const dayIndex = (dateObj.getDay() + 6) % 7; // Mon=0
              const label = DAY_LABELS[dayIndex];
              if (label) {
                dayMap[label] += item.totalMinutes
                  ? formatHours(item.totalMinutes)
                  : (item.totalHours || item.hours || 0);
              }
            }
          });

          setWeeklyData(DAY_LABELS.map((day) => ({
            day,
            hours: Math.round(dayMap[day] * 10) / 10,
          })));
        } else {
          setWeeklyData(DAY_LABELS.map((day) => ({ day, hours: 0 })));
        }

        // Process project data for top 5
        if (projectResp.status === 'fulfilled') {
          const raw = projectResp.value?.data || projectResp.value || {};
          const items = Array.isArray(raw) ? raw : (raw?.projects || []);
          const sorted = items
            .map((item: any, i: number) => ({
              name: item.projectName || item.name || 'Unknown',
              hours: item.totalHours || item.hours || (item.totalMinutes ? formatHours(item.totalMinutes) : 0),
              color: PROJECT_COLORS[i % PROJECT_COLORS.length],
            }))
            .sort((a: ProjectChartData, b: ProjectChartData) => b.hours - a.hours)
            .slice(0, 5);
          setProjectData(sorted);

          // Billable breakdown from project data
          const totalBillable = items.reduce(
            (sum: number, it: any) => sum + (it.billableHours || 0), 0
          );
          const totalNonBillable = items.reduce(
            (sum: number, it: any) => sum + (it.nonBillableHours || (it.totalHours || 0) - (it.billableHours || 0)), 0
          );
          setBillableData([
            { name: 'Billable', value: Math.round(totalBillable * 10) / 10 },
            { name: 'Non-Billable', value: Math.round(Math.max(0, totalNonBillable) * 10) / 10 },
          ]);
        } else {
          setProjectData([]);
          setBillableData([
            { name: 'Billable', value: 0 },
            { name: 'Non-Billable', value: 0 },
          ]);
        }
      } catch {
        // Graceful fallback for chart data
        setWeeklyData(DAY_LABELS.map((day) => ({ day, hours: 0 })));
        setProjectData([]);
        setBillableData([
          { name: 'Billable', value: 0 },
          { name: 'Non-Billable', value: 0 },
        ]);
      } finally {
        setChartsLoading(false);
      }
    };
    fetchChartData();
  }, [weekStart, weekEnd, monthStart, today]);

  // Fetch team dashboard when viewMode changes
  useEffect(() => {
    if (viewMode !== 'team') return;
    const fetchTeamDashboard = async () => {
      setTeamLoading(true);
      try {
        const response = await apiService.getTeamDashboard();
        setTeamStats(response.data || response);
      } catch (err: any) {
        showError(err.message || 'Failed to load team dashboard');
      } finally {
        setTeamLoading(false);
      }
    };
    fetchTeamDashboard();
  }, [viewMode, showError]);

  if (isLoading) return <LoadingSpinner message="Loading dashboard..." />;

  const firstName = user?.firstName || 'there';
  const activeProjectCount = projects.filter((p) => p.status === 'active').length;

  const statCards = [
    {
      icon: <Today sx={{ fontSize: 28 }} />,
      value: stats?.todayHours?.toFixed(1) || '0.0',
      label: "Today's Hours",
      suffix: 'h',
      gradient: 'linear-gradient(135deg, rgba(123, 109, 246, 0.15), rgba(123, 109, 246, 0.05))',
      iconBg: 'rgba(123, 109, 246, 0.15)',
      color: CHART_COLORS.primary,
    },
    {
      icon: <DateRange sx={{ fontSize: 28 }} />,
      value: stats?.weekHours?.toFixed(1) || '0.0',
      label: 'This Week',
      suffix: 'h',
      gradient: 'linear-gradient(135deg, rgba(16, 185, 129, 0.15), rgba(16, 185, 129, 0.05))',
      iconBg: 'rgba(16, 185, 129, 0.15)',
      color: CHART_COLORS.secondary,
    },
    {
      icon: stats?.hasRunningTimer
        ? <Timer sx={{ fontSize: 28 }} />
        : <TrendingUp sx={{ fontSize: 28 }} />,
      value: stats?.hasRunningTimer ? 'Active' : 'Idle',
      label: 'Timer Status',
      suffix: '',
      gradient: stats?.hasRunningTimer
        ? 'linear-gradient(135deg, rgba(245, 158, 11, 0.15), rgba(245, 158, 11, 0.05))'
        : 'linear-gradient(135deg, rgba(59, 130, 246, 0.15), rgba(59, 130, 246, 0.05))',
      iconBg: stats?.hasRunningTimer
        ? 'rgba(245, 158, 11, 0.15)'
        : 'rgba(59, 130, 246, 0.15)',
      color: stats?.hasRunningTimer ? CHART_COLORS.amber : CHART_COLORS.blue,
    },
    {
      icon: <FolderOpen sx={{ fontSize: 28 }} />,
      value: activeProjectCount,
      label: 'Active Projects',
      suffix: '',
      gradient: 'linear-gradient(135deg, rgba(244, 63, 94, 0.15), rgba(244, 63, 94, 0.05))',
      iconBg: 'rgba(244, 63, 94, 0.15)',
      color: CHART_COLORS.rose,
    },
  ];

  const quickActions = [
    { label: 'Start Timer', icon: <PlayArrow />, path: '/time', variant: 'contained' as const },
    { label: 'View Reports', icon: <Assessment />, path: '/reports', variant: 'outlined' as const },
    { label: 'Manage Team', icon: <Groups />, path: '/team', variant: 'outlined' as const },
  ];

  const hasBillableData = billableData.some((d) => d.value > 0);
  const hasProjectData = projectData.length > 0;

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 4, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight={700}>
            {getGreeting()}, {firstName}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
            {viewMode === 'personal'
              ? <>Here is your time tracking overview for {format(weekStart, 'MMM d')} - {format(weekEnd, 'MMM d, yyyy')}.</>
              : 'Team-wide performance and metrics overview.'
            }
          </Typography>
        </Box>
        {isManager && (
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(_, v) => { if (v) setViewMode(v); }}
            size="small"
          >
            <ToggleButton value="personal">Personal</ToggleButton>
            <ToggleButton value="team">Team</ToggleButton>
          </ToggleButtonGroup>
        )}
      </Box>

      {viewMode === 'personal' && (
      <>
      {/* Stat Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {statCards.map((stat, i) => (
          <Grid item xs={12} sm={6} md={3} key={i}>
            <Card sx={{
              background: stat.gradient,
              border: '1px solid rgba(148, 163, 184, 0.08)',
              transition: 'all 0.3s ease',
              '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 8px 32px rgba(0,0,0,0.15)' },
            }}>
              <CardContent sx={{ py: 3 }}>
                <Box sx={{
                  width: 44, height: 44, borderRadius: '12px', bgcolor: stat.iconBg,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  mb: 2, color: stat.color,
                }}>
                  {stat.icon}
                </Box>
                <Typography variant="h3" fontWeight={700} sx={{ mb: 0.5 }}>
                  {stat.value}{stat.suffix}
                </Typography>
                <Typography variant="body2" color="text.secondary">{stat.label}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Charts Row: Weekly Hours + Billable Donut */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Daily Hours This Week */}
        <Grid item xs={12} md={8}>
          <Card sx={{ p: 3, height: 380 }}>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Daily Hours This Week
            </Typography>
            {chartsLoading ? (
              <Skeleton variant="rectangular" height={280} sx={{ borderRadius: 2, mt: 2 }} />
            ) : (
              <ResponsiveContainer width="100%" height={290}>
                <BarChart data={weeklyData} margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
                  <XAxis
                    dataKey="day"
                    tick={{ fill: '#94a3b8', fontSize: 13 }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{ fill: '#94a3b8', fontSize: 12 }}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={(v) => `${v}h`}
                  />
                  <Tooltip content={<ChartTooltip />} cursor={{ fill: 'rgba(123, 109, 246, 0.08)' }} />
                  <Bar dataKey="hours" fill={CHART_COLORS.primary} radius={[6, 6, 0, 0]} maxBarSize={48} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </Card>
        </Grid>

        {/* Billable vs Non-Billable Donut */}
        <Grid item xs={12} md={4}>
          <Card sx={{ p: 3, height: 380 }}>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Billable vs Non-Billable
            </Typography>
            <Typography variant="caption" color="text.secondary">This month</Typography>
            {chartsLoading ? (
              <Skeleton variant="circular" width={180} height={180} sx={{ mx: 'auto', mt: 4 }} />
            ) : hasBillableData ? (
              <ResponsiveContainer width="100%" height={270}>
                <PieChart>
                  <Pie
                    data={billableData}
                    cx="50%"
                    cy="45%"
                    innerRadius={60}
                    outerRadius={90}
                    paddingAngle={4}
                    dataKey="value"
                    strokeWidth={0}
                  >
                    {billableData.map((_, idx) => (
                      <Cell key={idx} fill={PIE_COLORS[idx]} />
                    ))}
                  </Pie>
                  <Legend
                    verticalAlign="bottom"
                    formatter={(value: string, entry: any) => (
                      <span style={{ color: '#94a3b8', fontSize: 13 }}>
                        {value}: {entry.payload.value}h
                      </span>
                    )}
                  />
                  <Tooltip
                    formatter={(value: number) => [`${value.toFixed(1)}h`, '']}
                    contentStyle={{
                      backgroundColor: '#1a2332', border: '1px solid rgba(148,163,184,0.12)',
                      borderRadius: 8, color: '#f1f5f9',
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 270 }}>
                <Typography variant="body2" color="text.disabled">
                  No time data this month yet.
                </Typography>
              </Box>
            )}
          </Card>
        </Grid>
      </Grid>

      {/* Bottom Row: Top Projects + Quick Actions */}
      <Grid container spacing={3}>
        {/* Top Projects */}
        <Grid item xs={12} md={8}>
          <Card sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Top Projects
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
              Hours tracked this month
            </Typography>
            {chartsLoading ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                {[1, 2, 3].map((i) => (
                  <Skeleton key={i} variant="rectangular" height={28} sx={{ borderRadius: 1 }} />
                ))}
              </Box>
            ) : hasProjectData ? (
              <ResponsiveContainer width="100%" height={Math.max(200, projectData.length * 52)}>
                <BarChart
                  data={projectData}
                  layout="vertical"
                  margin={{ top: 4, right: 40, left: 0, bottom: 4 }}
                >
                  <XAxis
                    type="number"
                    tick={{ fill: '#94a3b8', fontSize: 12 }}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={(v) => `${v}h`}
                  />
                  <YAxis
                    type="category"
                    dataKey="name"
                    tick={{ fill: '#f1f5f9', fontSize: 13 }}
                    width={140}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip content={<ChartTooltip />} cursor={{ fill: 'rgba(123, 109, 246, 0.06)' }} />
                  <Bar dataKey="hours" radius={[0, 6, 6, 0]} maxBarSize={32}>
                    {projectData.map((entry, idx) => (
                      <Cell key={idx} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 6 }}>
                <Box sx={{ textAlign: 'center' }}>
                  <FolderOpen sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                  <Typography variant="body2" color="text.disabled">
                    No project data this month. Start tracking time to see your top projects.
                  </Typography>
                </Box>
              </Box>
            )}
          </Card>
        </Grid>

        {/* Quick Actions */}
        <Grid item xs={12} md={4}>
          <Card sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Quick Actions
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
              {quickActions.map((action) => (
                <Button
                  key={action.label}
                  variant={action.variant}
                  fullWidth
                  startIcon={action.icon}
                  onClick={() => navigate(action.path)}
                  sx={{ justifyContent: 'flex-start', py: 1.5 }}
                >
                  {action.label}
                </Button>
              ))}
            </Box>

            {/* Week summary mini stats */}
            <Box sx={{
              mt: 3, pt: 3, borderTop: '1px solid', borderColor: 'divider',
            }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Week at a Glance
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">Entries today</Typography>
                  <Typography variant="body2" fontWeight={600}>{stats?.todayEntries || 0}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">Entries this week</Typography>
                  <Typography variant="body2" fontWeight={600}>{stats?.weekEntries || 0}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">Active projects</Typography>
                  <Typography variant="body2" fontWeight={600}>{activeProjectCount}</Typography>
                </Box>
              </Box>
            </Box>
          </Card>
        </Grid>
      </Grid>
      </>
      )}

      {viewMode === 'team' && (
        teamLoading ? (
          <Box>
            <Grid container spacing={3} sx={{ mb: 4 }}>
              {[1, 2, 3, 4].map((i) => (
                <Grid item xs={12} sm={6} md={3} key={i}>
                  <Skeleton variant="rounded" height={140} />
                </Grid>
              ))}
            </Grid>
          </Box>
        ) : teamStats ? (
          <>
            {/* Team Stat Cards */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{
                  background: 'linear-gradient(135deg, rgba(123, 109, 246, 0.15), rgba(123, 109, 246, 0.05))',
                  border: '1px solid rgba(148, 163, 184, 0.08)',
                }}>
                  <CardContent sx={{ py: 3 }}>
                    <Box sx={{ width: 44, height: 44, borderRadius: '12px', bgcolor: 'rgba(123, 109, 246, 0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2, color: CHART_COLORS.primary }}>
                      <Groups sx={{ fontSize: 28 }} />
                    </Box>
                    <Typography variant="h3" fontWeight={700}>{teamStats.memberCount || 0}</Typography>
                    <Typography variant="body2" color="text.secondary">Team Members</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{
                  background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.15), rgba(16, 185, 129, 0.05))',
                  border: '1px solid rgba(148, 163, 184, 0.08)',
                }}>
                  <CardContent sx={{ py: 3 }}>
                    <Box sx={{ width: 44, height: 44, borderRadius: '12px', bgcolor: 'rgba(16, 185, 129, 0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2, color: CHART_COLORS.secondary }}>
                      <DateRange sx={{ fontSize: 28 }} />
                    </Box>
                    <Typography variant="h3" fontWeight={700}>{teamStats.totalWeekHours?.toFixed(1) || '0.0'}h</Typography>
                    <Typography variant="body2" color="text.secondary">Team Hours This Week</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{
                  background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.15), rgba(59, 130, 246, 0.05))',
                  border: '1px solid rgba(148, 163, 184, 0.08)',
                }}>
                  <CardContent sx={{ py: 3 }}>
                    <Box sx={{ width: 44, height: 44, borderRadius: '12px', bgcolor: 'rgba(59, 130, 246, 0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2, color: CHART_COLORS.blue }}>
                      <TrendingUp sx={{ fontSize: 28 }} />
                    </Box>
                    <Typography variant="h3" fontWeight={700}>{teamStats.utilizationPercent?.toFixed(0) || '0'}%</Typography>
                    <Typography variant="body2" color="text.secondary">Utilization Rate</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{
                  background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.15), rgba(245, 158, 11, 0.05))',
                  border: '1px solid rgba(148, 163, 184, 0.08)',
                }}>
                  <CardContent sx={{ py: 3 }}>
                    <Box sx={{ width: 44, height: 44, borderRadius: '12px', bgcolor: 'rgba(245, 158, 11, 0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2, color: CHART_COLORS.amber }}>
                      <FolderOpen sx={{ fontSize: 28 }} />
                    </Box>
                    <Typography variant="h3" fontWeight={700}>{teamStats.activeProjectCount || activeProjectCount}</Typography>
                    <Typography variant="body2" color="text.secondary">Active Projects</Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Team member breakdown */}
            {teamStats.members && teamStats.members.length > 0 && (
              <Card sx={{ p: 3 }}>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  Team Member Hours
                </Typography>
                <ResponsiveContainer width="100%" height={Math.max(250, (teamStats.members?.length || 0) * 44)}>
                  <BarChart
                    data={(teamStats.members || []).map((m: any, i: number) => ({
                      name: m.name || m.userName || 'Unknown',
                      hours: m.totalHours || m.weekHours || 0,
                      color: PROJECT_COLORS[i % PROJECT_COLORS.length],
                    }))}
                    layout="vertical"
                    margin={{ top: 4, right: 40, left: 0, bottom: 4 }}
                  >
                    <XAxis type="number" tick={{ fill: '#94a3b8', fontSize: 12 }} axisLine={false} tickLine={false} tickFormatter={(v) => `${v}h`} />
                    <YAxis type="category" dataKey="name" tick={{ fill: '#f1f5f9', fontSize: 13 }} width={140} axisLine={false} tickLine={false} />
                    <Tooltip content={<ChartTooltip />} cursor={{ fill: 'rgba(123, 109, 246, 0.06)' }} />
                    <Bar dataKey="hours" radius={[0, 6, 6, 0]} maxBarSize={32}>
                      {(teamStats.members || []).map((_: any, idx: number) => (
                        <Cell key={idx} fill={PROJECT_COLORS[idx % PROJECT_COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Card>
            )}
          </>
        ) : (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Groups sx={{ fontSize: 56, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">No team data available</Typography>
          </Box>
        )
      )}
    </Container>
  );
};

export default DashboardPage;
